import React, { useCallback, useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconDownload,
  IconFile,
  IconPlus,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';

import {
  DOCUMENT_TYPES,
  lookup,
  dateText,
  sizeText,
} from '../../api/constantsRealEstate';
import { realEstateApi as api, errorMessage } from '../../api/apiRealEstate';
import {
  Empty,
  ErrorMessage,
  Loading,
  PageHead,
} from '../../components/RealEstate/Shared';
import { UploadDocumentModal } from '../../components/RealEstate/Forms';

export default function DocumentsPage() {
  // App.js'den api, canEdit veya başka bir prop beklemiyoruz.
  const role = String(localStorage.getItem('user_role') ?? '')
    .toLocaleLowerCase('tr-TR');
  const canEdit = ['admin', 'yönetici'].includes(role);

  const [docs, setDocs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [documentType, setDocumentType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);

  const onMutated = useCallback(() => {
    setRefreshKey((value) => value + 1);
  }, []);

  // Evrak filtresindeki gayrimenkul seçeneklerini getir.
  useEffect(() => {
    let active = true;

    api.allPropertyOptions()
      .then((list) => {
        if (active) setProperties(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (active) setError(errorMessage(err));
      });

    return () => {
      active = false;
    };
  }, [refreshKey]);

  // Filtre veya veri değiştikçe evrakları getir.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    api.documents({
      property_id: selectedProperty ? Number(selectedProperty) : undefined,
      document_type: documentType || undefined,
    })
      .then((result) => {
        if (!active) return;
        setDocs(Array.isArray(result) ? result : (result?.items ?? []));
      })
      .catch((err) => {
        if (active) setError(errorMessage(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedProperty, documentType, refreshKey, nonce]);

  const propertyName = (id) =>
    properties.find((p) => p.value === String(id))?.label ||
    `Gayrimenkul #${id}`;

  async function handleDownload(document) {
    setBusy(true);
    setError('');
    try {
      await api.downloadDocument(document);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleUpload(propertyId, formData) {
    // Hata durumunda modal kendi hata yönetimini sürdürebilsin.
    await api.uploadDocument(propertyId, formData);
    onMutated();
    setUploadOpen(false);
  }

  async function handleDelete() {
    if (!deleteDoc) return;

    setBusy(true);
    setError('');
    try {
      await api.deleteDocument(deleteDoc.id);
      setDeleteDoc(null); // Yalnızca silme başarılıysa modalı kapat.
      onMutated();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        eyebrow="Belge arşivi"
        title="Evrak Yönetimi"
        description="Tapu, imar, sözleşme ve diğer dosyalar tek noktada."
        action={
          canEdit && (
            <Button
              leftSection={<IconPlus size={17} />}
              onClick={() => setUploadOpen(true)}
            >
              Evrak yükle
            </Button>
          )
        }
      />

      <Paper withBorder radius="lg" p="md" mb="md">
        <Group align="end" gap="sm">
          <Select
            searchable
            clearable
            label="Gayrimenkul"
            placeholder="Tüm gayrimenkuller"
            value={selectedProperty}
            data={properties}
            onChange={setSelectedProperty}
            style={{ flex: 2, minWidth: 200 }}
          />
          <Select
            clearable
            label="Evrak türü"
            placeholder="Tüm belgeler"
            value={documentType}
            data={DOCUMENT_TYPES}
            onChange={setDocumentType}
            style={{ flex: 1, minWidth: 160 }}
          />
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="default"
            onClick={() => setNonce((n) => n + 1)}
          >
            Yenile
          </Button>
        </Group>
      </Paper>

      <ErrorMessage
        message={error}
        onRetry={() => setNonce((n) => n + 1)}
      />

      {loading ? (
        <Loading />
      ) : docs.length === 0 ? (
        <Empty
          title="Evrak bulunamadı"
          description="Seçilen filtrelere uygun belge kaydı bulunmuyor."
        />
      ) : (
        <Paper withBorder radius="lg" style={{ overflow: 'hidden' }}>
          <ScrollArea>
            <Table miw={760} verticalSpacing="md" highlightOnHover className="re-table">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Dosya</Table.Th>
                  <Table.Th>Gayrimenkul</Table.Th>
                  <Table.Th>Tür</Table.Th>
                  <Table.Th>Yükleme tarihi</Table.Th>
                  <Table.Th>Boyut</Table.Th>
                  <Table.Th>İşlem</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {docs.map((document) => (
                  <Table.Tr key={document.id}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <IconFile size={18} color="#64748b" />
                        <Box style={{ minWidth: 0 }}>
                          <Text size="sm" fw={500} truncate maw={220}>
                            {document.original_name}
                          </Text>
                          {document.note && (
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {document.note}
                            </Text>
                          )}
                        </Box>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{propertyName(document.property_id)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light">
                        {lookup(DOCUMENT_TYPES, document.document_type)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{dateText(document.created_at)}</Text>
                    </Table.Td>
                    <Table.Td>{sizeText(document.file_size)}</Table.Td>
                    <Table.Td>
                      <Group gap={2} wrap="nowrap">
                        <Tooltip label="İndir">
                          <ActionIcon
                            variant="subtle"
                            disabled={busy}
                            onClick={() => handleDownload(document)}
                            aria-label="Evrakı indir"
                          >
                            <IconDownload size={17} />
                          </ActionIcon>
                        </Tooltip>
                        {canEdit && (
                          <Tooltip label="Sil">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              disabled={busy}
                              onClick={() => setDeleteDoc(document)}
                              aria-label="Evrakı sil"
                            >
                              <IconTrash size={17} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}

      <Text size="xs" c="dimmed" mt="sm">
        Not: Mevcut backend evrak listesini en fazla 500 kayıt ile döndürüyor.
        Büyük arşivlerde sayfalama API'si eklenmelidir.
      </Text>

      <UploadDocumentModal
        opened={uploadOpen}
        onClose={() => setUploadOpen(false)}
        properties={properties}
        onSave={handleUpload}
      />

      <Modal
        opened={Boolean(deleteDoc)}
        onClose={() => !busy && setDeleteDoc(null)}
        centered
        title="Evrak silinsin mi?"
        closeOnEscape={!busy}
        closeOnClickOutside={!busy}
      >
        <Text size="sm">
          {deleteDoc?.original_name} kalıcı olarak silinecek.
        </Text>
        <Group justify="end" mt="lg">
          <Button
            variant="default"
            disabled={busy}
            onClick={() => setDeleteDoc(null)}
          >
            Vazgeç
          </Button>
          <Button loading={busy} color="red" onClick={handleDelete}>
            Sil
          </Button>
        </Group>
      </Modal>
    </>
  );
}
